<template>
  <div class="aa-ad-landing">
    <div class="aa-ad-landing__stage">
      <div class="aa-ad-landing__wrap">
        <div class="aa-ad-landing__logo">
          <router-link :to="{ name: 'Home' }">
            <img
              class="logo"
              src="/images/AA-logo.svg"
              alt="MD Art Galleries"
            />
          </router-link>
        </div>

        <div class="aa-ad-landing__card">
          <h1>You found one of thousands.</h1>
          <p class="aa-ad-landing__sub">
            Every piece in the MD Art Galleries collection is scanned with
            ultra-high resolution — museum-quality, and ready to print at any
            size you need.
          </p>
          <p class="aa-ad-landing__cta-line">
            Click an option below to keep exploring.
          </p>

          <div class="aa-ad-landing__options">
            <router-link
              class="aa-ad-landing__opt"
              :to="{ name: 'Home' }"
              @click.native="trackFunnelClick('homepage')"
            >
              <span class="aa-ad-landing__opt-icon">
                <svg
                  width="19"
                  height="19"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#9D2235"
                  stroke-width="1.8"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="M3 11l9-7 9 7" />
                  <path d="M5 10v9a1 1 0 0 0 1 1h4v-6h4v6h4a1 1 0 0 0 1-1v-9" />
                </svg>
              </span>
              <span class="aa-ad-landing__opt-text">
                <span class="aa-ad-landing__opt-title">Visit our homepage</span>
                <span class="aa-ad-landing__opt-desc"
                  >See what's new and featured</span
                >
              </span>
              <svg
                class="aa-ad-landing__opt-arrow"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#9D2235"
                stroke-width="2.2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M9 6l6 6-6 6" />
              </svg>
            </router-link>

            <router-link
              class="aa-ad-landing__opt"
              :to="{ name: 'ArtCategories' }"
              @click.native="trackFunnelClick('search')"
            >
              <span class="aa-ad-landing__opt-icon">
                <svg
                  width="19"
                  height="19"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#9D2235"
                  stroke-width="1.8"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <circle cx="10.5" cy="10.5" r="6.5" />
                  <path d="M20 20l-4.8-4.8" />
                </svg>
              </span>
              <span class="aa-ad-landing__opt-text">
                <span class="aa-ad-landing__opt-title">Search by keyword</span>
                <span class="aa-ad-landing__opt-desc"
                  >Looking for a subject, color, or style?</span
                >
              </span>
              <svg
                class="aa-ad-landing__opt-arrow"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#9D2235"
                stroke-width="2.2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M9 6l6 6-6 6" />
              </svg>
            </router-link>

            <router-link
              v-if="artistSlug"
              class="aa-ad-landing__opt"
              :to="{ name: 'artist', params: { artist: artistSlug } }"
              @click.native="trackFunnelClick('artist')"
            >
              <span class="aa-ad-landing__opt-icon">
                <svg
                  width="19"
                  height="19"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#9D2235"
                  stroke-width="1.8"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <circle cx="12" cy="8" r="3.5" />
                  <path d="M5 20c0-4 3-6.5 7-6.5s7 2.5 7 6.5" />
                </svg>
              </span>
              <span class="aa-ad-landing__opt-text">
                <span class="aa-ad-landing__opt-title"
                  >See more from this artist</span
                >
                <span class="aa-ad-landing__opt-desc"
                  >{{ artistName }}'s full collection</span
                >
              </span>
              <svg
                class="aa-ad-landing__opt-arrow"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#9D2235"
                stroke-width="2.2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M9 6l6 6-6 6" />
              </svg>
            </router-link>
          </div>

          <p class="aa-ad-landing__shipping">
            Free shipping across Maryland, DC &amp; Virginia.
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { trackEvent } from "@/utils/analytics";

export default {
  name: "AdLanding",
  data: () => ({
    // Which artist's link shows in the third option. Set via a query param
    // (?artist=Galina_Ponomarenko) so this one page can serve different ad
    // creatives without a code change per campaign.
    artistSlug: "",
    artistName: ""
  }),
  created() {
    const query = this.$route.query;
    if (query.artist) {
      this.artistSlug = query.artist;
      this.artistName = String(query.artist).replace(/_/g, " ");
    }
    document.title = "MD Art Galleries";

    this.$store.dispatch("loader/showLoader", {
      loading: false
    });
  },
  methods: {
    // Fires when a visitor picks one of the funnel options, routed through
    // the shared helper so it lands in GA4 (for a full ad->site->purchase
    // funnel view) as well as Meta (as a Lead conversion event).
    trackFunnelClick(option) {
      trackEvent("funnel_option_click", {
        option: option,
        artist: this.artistSlug || undefined
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.aa-ad-landing {
  min-height: 100vh;
  width: 100%;
}
.aa-ad-landing__stage {
  min-height: 100vh;
  width: 100%;
  background: radial-gradient(
    120% 120% at 50% 0%,
    #fbe4e2 0%,
    #fbe9d6 40%,
    #fffcfa 75%
  );
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
}
.aa-ad-landing__wrap {
  width: 100%;
  max-width: 440px;
}
.aa-ad-landing__logo {
  display: flex;
  justify-content: center;
  margin-bottom: 22px;

  img {
    height: 34px;
    width: auto;
  }
}
.aa-ad-landing__card {
  background: #ffffff;
  border-radius: 20px;
  padding: 34px 32px;
  box-shadow: 0 24px 48px -18px rgba(23, 16, 22, 0.22);

  h1 {
    font-size: 21px;
    font-weight: 800;
    text-align: center;
    line-height: 1.35;
    color: #171016;
    margin: 0 0 10px;
  }
}
.aa-ad-landing__sub {
  font-size: 14px;
  line-height: 1.6;
  text-align: center;
  color: #5c4a4f;
  margin: 0 0 8px;
}
.aa-ad-landing__cta-line {
  font-size: 13px;
  line-height: 1.5;
  text-align: center;
  color: #9d2235;
  font-weight: 600;
  margin: 0 0 26px;
}
.aa-ad-landing__options {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.aa-ad-landing__opt {
  display: flex;
  align-items: center;
  gap: 14px;
  background: #fffcfa;
  border: 1.5px solid rgba(36, 20, 23, 0.12);
  border-radius: 14px;
  padding: 15px 16px;
  text-decoration: none;
  color: #241417;
  transition: border-color 0.15s, background 0.15s;

  &:hover {
    border-color: #9d2235;
    background: #fbf1ee;
  }
}
.aa-ad-landing__opt-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: #fbe4e2;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.aa-ad-landing__opt-text {
  flex-grow: 1;
  min-width: 0;
}
.aa-ad-landing__opt-title {
  display: block;
  font-size: 14.5px;
  font-weight: 700;
}
.aa-ad-landing__opt-desc {
  display: block;
  font-size: 12.5px;
  color: #7a6a6e;
  margin-top: 1px;
}
.aa-ad-landing__opt-arrow {
  flex-shrink: 0;
}
.aa-ad-landing__shipping {
  font-size: 12px;
  text-align: center;
  color: #a89599;
  margin: 22px 0 0;
}

@media (max-width: 480px) {
  .aa-ad-landing__card {
    padding: 30px 22px;
    border-radius: 0;
    min-height: 100vh;
  }
  .aa-ad-landing__stage {
    padding: 0;
  }
}
</style>
